import { RootState } from '../../store'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import IBet from '../../../models/IBet'
import { getPlaceBetAction } from './bet.action'
import { IApiStatus } from '../../../models/IApiStatus'
import {
  clearPersistedCashoutLockKeys,
  loadPersistedCashoutLockKeys,
  persistCashoutLockKeys,
} from '../../../utils/cashout-lock-storage'
import { logout } from '../login/loginSlice'

export type FancyBook = { matchId: number; selectionId: string; marketName: string }

/** Redux key for per-market cashout lock (after cashout until a new normal bet). */
export function cashoutMarketLockKey(
  matchId: number | string | undefined | null,
  marketId: string | undefined | null,
): string {
  return `${matchId ?? ''}::${String(marketId ?? '')}`
}

export type BetState = {
  bet: {
    isOpen: boolean
    betData: IBet
  }
  placeBet: {
    bet: IBet
    status: string
    error: any
  }
  betCount: number
  fancyType: string
  userBookMarketList: object
  fancyMatchAndSelectionId: FancyBook
  marketBookAndSelectionId: FancyBook
  betlist: any
  /**
   * Keys `${matchId}::${marketId}` where cashout was used; cashout UI stays off until a non-cashout bet in that market.
   */
  cashoutLockedMarketKeys: Record<string, true>
}

const initialState: BetState = {
  bet: {
    isOpen: false,
    betData: {} as IBet,
  },
  placeBet: {
    bet: {} as IBet,
    status: '',
    error: '',
  },
  betCount: 0,
  fancyType: 'session',
  userBookMarketList: {},
  fancyMatchAndSelectionId: {} as FancyBook,
  marketBookAndSelectionId: {} as FancyBook,
  betlist: [] as any,
  cashoutLockedMarketKeys: loadPersistedCashoutLockKeys(),
}

const betSlice = createSlice({
  name: 'bet',
  initialState,
  reducers: {
    betPopup: (
      state,
      action: PayloadAction<{
        isOpen: boolean
        betData: IBet
      }>,
    ) => {
      state.bet = action.payload
    },
    setBetCount: (state, action: PayloadAction<number>) => {
      state.betCount = action.payload
    },
    setFancyType: (state, action: PayloadAction<string>) => {
      state.fancyType = action.payload
    },
    setBookMarketList: (state, action: PayloadAction<Record<string, number>>) => {
      // Merge the new profit data with existing state instead of overwriting
      // This ensures fancy bet profit indicators persist
      state.userBookMarketList = { ...state.userBookMarketList, ...action.payload }
    },
    clearBookMarketList: (state) => {
      // Clear the market book when needed (e.g., when changing matches)
      state.userBookMarketList = {}
      state.cashoutLockedMarketKeys = {}
      persistCashoutLockKeys(state.cashoutLockedMarketKeys)
    },
    lockCashoutForMarket: (
      state,
      action: PayloadAction<{ matchId: number | string; marketId: string }>,
    ) => {
      state.cashoutLockedMarketKeys[
        cashoutMarketLockKey(action.payload.matchId, action.payload.marketId)
      ] = true
      persistCashoutLockKeys(state.cashoutLockedMarketKeys)
    },
    clearCashoutLockForMarket: (
      state,
      action: PayloadAction<{ matchId: number | string; marketId: string }>,
    ) => {
      delete state.cashoutLockedMarketKeys[
        cashoutMarketLockKey(action.payload.matchId, action.payload.marketId)
      ]
      persistCashoutLockKeys(state.cashoutLockedMarketKeys)
    },
    setBookFancy: (state, action: PayloadAction<FancyBook>) => {
      state.fancyMatchAndSelectionId = action.payload
    },
    setMarketBookUser: (state, action: PayloadAction<FancyBook>) => {
      state.marketBookAndSelectionId = action.payload
    },
    setbetlist: (state, action: PayloadAction<FancyBook>) => {
      state.betlist = action.payload
    },
    /** After SPA login, session exists in localStorage but the store may have initialized empty. */
    rehydrateCashoutLocksFromStorage: (state) => {
      state.cashoutLockedMarketKeys = loadPersistedCashoutLockKeys()
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, (state) => {
        state.cashoutLockedMarketKeys = {}
        clearPersistedCashoutLockKeys()
      })
      .addCase(getPlaceBetAction.pending, (state) => {
        state.placeBet.status = IApiStatus.Loading
      })
      .addCase(getPlaceBetAction.fulfilled, (state, action) => {
        state.placeBet.bet = action.payload
        state.placeBet.error = ''
        state.placeBet.status = IApiStatus.Done
      })
      .addCase(getPlaceBetAction.rejected, (state, action) => {
        state.placeBet.error = action.payload
        state.placeBet.status = IApiStatus.Fail
      })
  },
})

export const {
  betPopup,
  setBetCount,
  setFancyType,
  setBookMarketList,
  clearBookMarketList,
  lockCashoutForMarket,
  clearCashoutLockForMarket,
  setBookFancy,
  setMarketBookUser,
  setbetlist, /// specific for cmeter
  rehydrateCashoutLocksFromStorage,
} = betSlice.actions
export const selectBetPopup = (state: RootState) => state.betReducer.bet

export const selectPlaceBet = (state: RootState) => state.betReducer.placeBet

export const selectBetCount = (state: RootState) => state.betReducer.betCount

export const selectFancyType = (state: RootState) => state.betReducer.fancyType

export const selectMarketBook = (state: RootState) => state.betReducer.userBookMarketList

export const selectBookFancy = (state: RootState) => state.betReducer.fancyMatchAndSelectionId
export const selectBookMarketUser = (state: RootState) => state.betReducer.marketBookAndSelectionId
export const selectBetListUser = (state: RootState) => state.betReducer.betlist /// specific for cmeter

export const selectCashoutLockedMarketKeys = (state: RootState) =>
  state.betReducer.cashoutLockedMarketKeys

export default betSlice
